<script setup>
import { ref } from 'vue';

const isMenuOpen = ref(false);

// Function to close menu when a link is clicked
const closeMenu = () => {
  isMenuOpen.value = false;
}

// --- SMOOTH ANIMATION FUNCTIONS ---
const beforeEnter = (el) => {
  el.style.height = '0';
  el.style.opacity = '0';
}

const enter = (el) => {
  el.offsetHeight; // Force reflow
  el.style.height = el.scrollHeight + 'px';
  el.style.opacity = '1';
}

const afterEnter = (el) => {
  el.style.height = 'auto'; 
}

const beforeLeave = (el) => {
  el.style.height = el.scrollHeight + 'px';
  el.style.opacity = '1';
}

const leave = (el) => {
  el.offsetHeight; 
  el.style.height = '0';
  el.style.opacity = '0';
}
</script>

<template>
  <nav 
    class="fixed w-full z-20 top-0 start-0 border-b border-gray-200/15 bg-[#161616]"
  >
    <div class="max-w-screen-xl flex flex-wrap items-center justify-between mx-auto p-4">
      
      <router-link 
        :to="{ name: 'Home' }" 
        class="flex items-center space-x-3 rtl:space-x-reverse"
        @click="closeMenu"
      >
        <img src="@/assets/logo.svg" width="25" height="25" alt="Logo">
      </router-link>

      <div class="flex md:ml-auto md:order-2 space-x-3 md:space-x-0 rtl:space-x-reverse">
        <a 
          href="https://apps.apple.com/app/id6759008622" 
          target="_blank"
          rel="noopener noreferrer"
          class="hidden md:block"
        >
          <img 
            src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1234567890" 
            alt="Download on the App Store" 
            class="w-52 h-10"
          />
        </a>

        <button 
          @click="isMenuOpen = !isMenuOpen" 
          type="button" 
          class="inline-flex items-center p-2 w-10 h-10 justify-center text-sm text-gray-500 rounded-lg md:hidden hover:bg-gray-100/10 focus:outline-none focus:ring-2 focus:ring-gray-200/50"
        >
          <span class="sr-only">Open main menu</span>
          
          <transition name="icon-fade" mode="out-in">
            <svg 
              v-if="!isMenuOpen" 
              class="w-5 h-5" 
              key="menu" 
              aria-hidden="true" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 17 14"
            >
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M1 1h15M1 7h15M1 13h15"/>
            </svg>
            
            <svg 
              v-else 
              class="w-5 h-5" 
              key="close" 
              aria-hidden="true" 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 14 14"
            >
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
            </svg>
          </transition>
        </button>
      </div>

      <div class="hidden w-full md:block md:w-auto md:order-1 md:absolute md:left-1/2 md:transform md:-translate-x-1/2">
        <ul class="flex flex-col p-4 md:p-0 mt-4 font-medium md:space-x-8 rtl:space-x-reverse md:flex-row md:mt-0 md:border-0 items-center">
          <li>
            <router-link :to="{ name: 'Home' }" class="nav-link font-regular" exact-active-class="active-link">ABOUT</router-link>
          </li>
          <li>
             <router-link :to="{ name: 'PrivacyTerms' }" class="nav-link font-regular" active-class="active-link">TERMS & PRIVACY</router-link>
         </li>
          <li>
            <router-link :to="{ name: 'Help' }" class="nav-link font-regular" exact-active-class="active-link">HELP</router-link>
          </li>
        </ul>
      </div>

      <transition
        name="mobile-menu"
        @before-enter="beforeEnter"
        @enter="enter"
        @after-enter="afterEnter"
        @before-leave="beforeLeave"
        @leave="leave"
      >
        <div v-if="isMenuOpen" class="w-full md:hidden overflow-hidden mobile-menu-container">
          
          <div class="flex flex-col items-center pb-6">
            <ul class="flex flex-col w-full text-center mt-4 space-y-2">
              <li>
                <router-link 
                  :to="{ name: 'Home' }" 
                  class="mobile-link font-regular" 
                  exact-active-class="text-[#BFBCEC] font-bold"
                  @click="closeMenu"
                >
                  ABOUT
                </router-link>
              </li>
              <li>
                <router-link 
                  :to="{ name: 'PrivacyTerms' }" 
                  class="mobile-link font-regular" 
                  active-class="text-[#BFBCEC] font-bold"
                  @click="closeMenu"
                >
                  TERMS & PRIVACY
                </router-link>
              </li>
              <li>
                <router-link 
                  :to="{ name: 'Help' }" 
                  class="mobile-link font-regular" 
                  exact-active-class="text-[#BFBCEC] font-bold"
                  @click="closeMenu"
                >
                  HELP
                </router-link>
              </li>
            </ul>

            <div class="mt-6">
              <a 
                href="https://apps.apple.com/app/id6759008622" 
                target="_blank"
                rel="noopener noreferrer"
                @click="closeMenu"
              >
                <img 
                  src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1234567890" 
                  alt="Download on the App Store" 
                  class="h-10"
                />
              </a>
            </div>
          </div>

        </div>
      </transition>

    </div>
  </nav>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Albert+Sans:wght@400;900&display=swap');

.font-regular { font-family: 'Albert Sans', sans-serif; font-weight: 400; }
.font-bold { font-family: 'Albert Sans', sans-serif; font-weight: 900; }

/* Desktop Link Styles */
.nav-link {
  display: block; padding: 8px 12px; color: #E6E6E7; border-radius: 4px; transition: color 0.2s;
}
.nav-link:hover { color: #D0D0D1; }
.active-link { color: #BFBCEC; font-weight: 900; }

/* Mobile Link Styles */
.mobile-link { display: block; padding: 12px; color: #E6E6E7; font-size: 16px; }

/* ICON ANIMATION: 
  Rotates 90 degrees and scales slightly while fading
*/
.icon-fade-enter-active,
.icon-fade-leave-active {
  transition: all 0.2s ease;
}

.icon-fade-enter-from,
.icon-fade-leave-to {
  opacity: 0;
  transform: rotate(90deg) scale(0.5);
}

/* Menu Animation */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
  overflow: hidden;
}
</style>